/**
 * The roster projection: two server reads in, one table's worth of rows out.
 *
 * This is the entity's decision layer. Everything a screen would otherwise have to know — which
 * key is safe to render a row under, what a lower-case status string means, which attention item
 * belongs to which row, whether a client is churning, and the difference between "0%" and "not
 * measured" — is settled here, once, where a test can reach it without a renderer.
 *
 * Pure: no React, no DOM, no query hook, no i18next. `t` never arrives; what leaves this module
 * is a reason KEY the UI translates.
 *
 * It lives in `entities/` rather than in a page because two surfaces already project this same
 * response — the roster and the assign-plan picker (`assignableClients`) — and the KANON clients
 * panel adds a third and a fourth in the invite dialog's seat list and the archive's re-invite.
 *
 * Four things about the contract are load-bearing, all verified against the sources rather than
 * recalled:
 *
 * 1. `GET /customer` returns a BARE ARRAY with **no ordering guarantee** —
 *    `CustomerRepository.GetAllAsync` has no `OrderBy` at all, and pending invitations are
 *    appended at the tail. Postgres may reorder between calls. Ordering is entirely ours.
 * 2. **`id` is not a row's identity.** An invited row carries the real person's id since the
 *    15 August 2026 contract, but a row is still keyed on its invitation while it has one — the
 *    same key the attention queue's `InvitationPending` anchors on — and an older snapshot of this
 *    API sent `id: 0` on every invitation, so two outstanding ones collided on it. `rowKey` exists
 *    for this.
 * 3. The seven metric fields are `[JsonIgnore(WhenWritingNull)]`, so they are **absent keys**,
 *    not nulls — and `lastWorkoutAt` / `goal` / `planName` stay absent even with
 *    `includeMetrics=true` when the client has no completed workout / no goal / no plan. A
 *    pending invitation never carries metrics at all. So "absent" cannot mean "we did not ask";
 *    it means "there is nothing", and the table has to say that rather than show 0.
 * 4. `status` arrives LOWER-CASE, unlike every other enum this API emits.
 */

import type {
  TrainWithMeModulesAttentionDtosResponsesAttentionQueueItem as AttentionQueueItem,
  TrainWithMeModulesAttentionDtosResponsesAttentionQueueResponse as AttentionQueueResponse,
  TrainWithMeModulesClientsDtosResponsesGetCustomerResponse as CustomerDto,
  TrainWithMeModulesClientsDtosResponsesTrainingMoodReading as MoodReadingDto,
  TrainWithMeModulesClientsDtosResponsesTrainingSleepReading as SleepReadingDto,
} from '~/shared/api/generated/model';
import { calendarDaysSince } from '~/shared/lib/calendarDate';

import { clientStanding, type ClientStanding } from './planStanding';

/**
 * The wire's `status`, narrowed.
 *
 * Lower-case: `CustomerResponseMapper` does `customer.Status.ToString().ToLower()`, and the
 * invitation mapper hardcodes the literal `"invited"`. Handled by an explicit table rather than
 * by `toLowerCase()` on a guess, so a future PascalCase correction upstream fails loudly here
 * instead of silently classifying every row as unknown.
 */
export type ClientStatus = 'active' | 'invited' | 'inactive';

const STATUS_BY_WIRE_VALUE: Readonly<Record<string, ClientStatus>> = {
  active: 'active',
  invited: 'invited',
  inactive: 'inactive',
};

/**
 * Where each status word's copy comes from — and the reason the three are not one template.
 *
 * `active` and `invited` are mobile's own, reused verbatim so a trainer reads the same word on both
 * platforms. `inactive` no longer exists there: the 18 August 2026 locale re-sync brought mobile's
 * bundle forward and it had DROPPED `clients.status.inactive` along with the whole inactive filter,
 * which that app no longer offers. This one still does — the roster has a `Nieaktywni` tab — so the
 * word is web-owned now, in the `web:` namespace where `CLAUDE.md` § i18n puts copy for a surface
 * mobile does not have.
 *
 * Written as a table rather than `clients.status.${status}` because that template is what made the
 * gap invisible: an unmapped status renders its own key on screen, and nothing in the type system or
 * the build says so. Both roster tables read this, which is why it lives in the entity rather than
 * in either of them.
 */
const STATUS_LABEL_KEY: Readonly<Record<ClientStatus, string>> = {
  active: 'clients.status.active',
  invited: 'clients.status.invited',
  inactive: 'web:clients.status.inactive',
};

/** The i18n key naming this status, resolved against whichever bundle actually holds the word. */
export function clientStatusLabelKey(status: ClientStatus): string {
  return STATUS_LABEL_KEY[status];
}

/**
 * A wire status word, or `null` where this build has no name for it.
 *
 * `Object.hasOwn` rather than a bare index — the seventh and last lookup in this app keyed by a wire
 * string to be written this way. An object literal inherits `constructor` and the rest of
 * `Object.prototype`, so a wire value of `constructor` returns a FUNCTION that sails straight past
 * the `??` and arrives where a `ClientStatus` is expected. Not reachable today, since these are C#
 * enum names; the other six are written this way and a single exception is how the pattern stops
 * being one.
 */
function clientStatusOf(wireValue: string | null | undefined): ClientStatus | null {
  if (wireValue == null) return null;
  return Object.hasOwn(STATUS_BY_WIRE_VALUE, wireValue)
    ? STATUS_BY_WIRE_VALUE[wireValue] ?? null
    : null;
}

/**
 * The attention reasons this app can name.
 *
 * The wire sends the C# enum member name verbatim, so it is PascalCase and these are its lower-camel
 * twin. Fifteen reasons are live — every reason `AttentionReasons.All` registers with an anchor
 * prefix — and the enum's sixteenth member, `NewClient`, is retired: no detector emits it, and it is
 * deliberately absent below rather than mapped to a key that does not exist.
 *
 * Twelve of the fifteen take their words from mobile's verbatim bundle and three do not, because
 * that bundle has no key for them. Which scope a reason reads from is `attentionReasonCopy`'s
 * single decision, not a guess made at a call site.
 */
export type AttentionReason =
  | 'missedWorkout'
  | 'abandonedWorkout'
  | 'invitationPending'
  | 'goalRenewalDue'
  | 'planEndingSoon'
  | 'planCompletionDue'
  | 'unreadMessage'
  | 'noUpcomingSessions'
  | 'lapsedActivity'
  | 'sessionAwaitingReview'
  | 'surveyAwaitingReview'
  | 'goalAchieved'
  | 'planCompleted'
  | 'newClip'
  | 'clipCommentAwaitingReply'
  /**
   * Someone filled in the trainer's public form (public-form R20). Subject-less — the filler is not
   * a client — and returned only to a read that sends `includeLeadItems=true`, which only this app
   * does, so it never takes a slot in the phone's queue.
   */
  | 'publicFormLeadSubmitted'
  /**
   * The first GENERATION of reasons (attention-queue R26): the average of a client's last readings
   * of sleep or energy stands under the trainer's level (R22, R23), and a session that set a
   * personal record (R24). Sent only to a read that declares `reasonSet`, which
   * `attentionQueueQueryOptions` does.
   */
  | 'lowSleep'
  | 'lowEnergy'
  | 'prAchieved';

/**
 * Explicit, not `lowerFirst(reason)`. A derived conversion would happily mint
 * `clients.tasks.pills.newClient` for the retired member and render a raw key at the trainer, and
 * it would do the same for whatever reason the backend adds next. This table is the list of
 * reasons this app has strings for; anything outside it is dropped.
 */
const REASON_BY_WIRE_VALUE: Readonly<Record<string, AttentionReason>> = {
  MissedWorkout: 'missedWorkout',
  AbandonedWorkout: 'abandonedWorkout',
  InvitationPending: 'invitationPending',
  GoalRenewalDue: 'goalRenewalDue',
  PlanEndingSoon: 'planEndingSoon',
  PlanCompletionDue: 'planCompletionDue',
  UnreadMessage: 'unreadMessage',
  NoUpcomingSessions: 'noUpcomingSessions',
  LapsedActivity: 'lapsedActivity',
  SessionAwaitingReview: 'sessionAwaitingReview',
  SurveyAwaitingReview: 'surveyAwaitingReview',
  GoalAchieved: 'goalAchieved',
  PlanCompleted: 'planCompleted',
  NewClip: 'newClip',
  ClipCommentAwaitingReply: 'clipCommentAwaitingReply',
  PublicFormLeadSubmitted: 'publicFormLeadSubmitted',
  LowSleep: 'lowSleep',
  LowEnergy: 'lowEnergy',
  PrAchieved: 'prAchieved',
};

/**
 * Narrow a wire reason to one this app has strings for, or `null`.
 *
 * Exported on its second consumer, not its first: the roster groups the queue into per-row task
 * counts, and the client detail's Przegląd tab lists one client's items individually with their
 * anchors intact. Both have to answer "is this a reason we can name", and a second copy of
 * `REASON_BY_WIRE_VALUE` is how the two would drift the next time the backend adds a member.
 */
export function attentionReasonOf(wireValue: string | null | undefined): AttentionReason | null {
  if (wireValue == null) return null;
  // `Object.hasOwn`, not a bare index. Every lookup in this app that is KEYED BY A WIRE STRING is
  // written this way: an object literal inherits `constructor`, `toString` and the rest of
  // `Object.prototype`, so a wire value of `constructor` returns a FUNCTION that sails past `??`.
  // Here it would mislabel a row; in `muscleVolume` the same shape reached `.filter` and threw.
  // Not reachable today — these are C# enum names — so this is hardening rather than a fix.
  return Object.hasOwn(REASON_BY_WIRE_VALUE, wireValue) ? REASON_BY_WIRE_VALUE[wireValue] ?? null : null;
}

/**
 * The same table read backwards: the wire spelling to SEND for a reason this app names.
 *
 * It lives here, beside the table it inverts, rather than beside the one request that needs it —
 * `POST /attention-queue/responses`, which names the occurrence it suppresses by reason. That
 * request used to build the string by upper-casing the first letter, which is precisely the derived
 * conversion the header above forbids in the other direction: it answers for a reason this app has
 * no strings for as confidently as for one it does, and it would keep answering after the backend
 * renames a member.
 *
 * `attentionReasonRoundTripsToItsWireValue` in the test file beside this one is what keeps the two
 * halves from drifting apart.
 */
const WIRE_VALUE_BY_REASON: Readonly<Record<AttentionReason, string>> = {
  missedWorkout: 'MissedWorkout',
  abandonedWorkout: 'AbandonedWorkout',
  invitationPending: 'InvitationPending',
  goalRenewalDue: 'GoalRenewalDue',
  planEndingSoon: 'PlanEndingSoon',
  planCompletionDue: 'PlanCompletionDue',
  unreadMessage: 'UnreadMessage',
  noUpcomingSessions: 'NoUpcomingSessions',
  lapsedActivity: 'LapsedActivity',
  sessionAwaitingReview: 'SessionAwaitingReview',
  surveyAwaitingReview: 'SurveyAwaitingReview',
  goalAchieved: 'GoalAchieved',
  planCompleted: 'PlanCompleted',
  newClip: 'NewClip',
  clipCommentAwaitingReply: 'ClipCommentAwaitingReply',
  publicFormLeadSubmitted: 'PublicFormLeadSubmitted',
  lowSleep: 'LowSleep',
  lowEnergy: 'LowEnergy',
  prAchieved: 'PrAchieved',
};

/** How the backend spells `reason`, for a body this app sends. */
export function attentionReasonWireValue(reason: AttentionReason): string {
  return WIRE_VALUE_BY_REASON[reason];
}

/** The anchor prefix `InvitationPending` uses: `invitation:{invitationId}`. */
const INVITATION_ANCHOR_PREFIX = 'invitation:';

/**
 * How the attendance ring paints.
 *
 * A name rather than a colour, because `model/` decides and the stylesheet paints — and because
 * the two themes give the same tone two different values.
 */
export type ComplianceTone = 'good' | 'steady' | 'poor';

/**
 * The canon's own cuts, and they are NOT mobile's.
 *
 * KANON draws six rings — 92 % `--good`, 85 / 79 / 71 % `--accent`, 58 / 44 % `--danger` — while
 * mobile's `ComplianceRing` cuts at 75 / 50 into good / warn / danger, using amber where the
 * canon uses ember. Under mobile's rule three of the canon's six rings would change colour, so
 * the canon was followed.
 *
 * The canon's six samples do not pin the lower cut: 60 and 70 both reproduce it exactly. 60 is
 * taken because it keeps more of mobile's shape — there, everything from 50 up is "not danger".
 * Recorded in `docs/design/kanon-clients-2026-08-11/README.md` for the owner to confirm.
 */
const GOOD_FROM_PCT = 90;
const STEADY_FROM_PCT = 60;

export function complianceTone(pct: number): ComplianceTone {
  if (pct >= GOOD_FROM_PCT) return 'good';
  if (pct >= STEADY_FROM_PCT) return 'steady';
  return 'poor';
}

/**
 * One KIND of thing waiting on the trainer for one client, with how many of them there are.
 *
 * Grouped by reason rather than left as raw queue items, because the queue is an item log and a
 * summary is what a row can carry: a client with three missed sessions produced three
 * `MissedWorkout` items. The count is kept so the roster total still adds up to the number of
 * things there are to do, not the number of kinds.
 */
export interface ClientTask {
  readonly reason: AttentionReason;
  /** How many queue items of this reason this client has. At least 1. */
  readonly count: number;
  /** The backend's fixed per-reason weight, 3–9. Higher is more urgent. */
  readonly severity: number;
  /** The newest signal of this reason, or `null` if none carried a timestamp. */
  readonly latestSignalAtUtc: string | null;
}

/** What the attendance cell has to work with, or `null` when the row was never measured. */
export interface ClientCompliance {
  readonly pct: number;
  readonly done: number | null;
  readonly total: number | null;
  /** Percentage points against the previous window. Negative is a decline. */
  readonly trend: number | null;
  readonly tone: ComplianceTone;
}

/**
 * One night's sleep, or one session's mood, as the roster read sends it.
 *
 * Both series carry the SAME shape and the same guarantees, stated on `GetCustomerResponse` itself:
 * oldest first, at most the seven newest, from the last fourteen days, and only from sessions the
 * client actually finished. They are sparse and INDEPENDENT of one another — a session that logged
 * a mood and no sleep produces a mood point and no bar — so nothing here may assume the two are the
 * same length or line up by index.
 *
 * `value` is on the scale the CONTRACT states, not the canon's. Sleep is hours; mood is 1–10
 * (`TrainingMoodReading.Value`, "Mood on the 1-10 scale the client logged it on"), where KANON's
 * specimen draws a 1–5 rating. That difference is a projection's problem, not a mapping's, and
 * `pages/client-roster/model/wellbeingCells` is where it is answered.
 */
export interface WellbeingReading {
  readonly at: string;
  readonly value: number;
}

/**
 * A reading series, read defensively.
 *
 * The three metric fields are `[JsonIgnore(WhenWritingNull)]`, so an absent series is an ABSENT KEY
 * — a client who logged nothing, a pending invitation that carries no metrics at all, and a caller
 * that did not ask for them all look identical here. All three mean the same thing to a row: there
 * is nothing to draw. An entry missing its own figure is dropped rather than defaulted, because a
 * zero in a sleep strip is a claim about a night the client did not report on.
 */
function readingsOf(
  entries: readonly (SleepReadingDto | MoodReadingDto)[] | null | undefined,
): readonly WellbeingReading[] {
  const out: WellbeingReading[] = [];
  for (const entry of entries ?? []) {
    // `in` cannot narrow this union: both members declare their figure OPTIONAL, so `hours` is a
    // known-possible key on the mood reading too and the guard is a no-op to the compiler. Reading
    // both and taking whichever is a number is the honest spelling, and it is total — an entry
    // carrying neither is dropped by the check below.
    const value = 'hours' in entry ? entry.hours : 'value' in entry ? entry.value : undefined;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    if (typeof entry.at !== 'string' || entry.at === '') continue;
    out.push({ at: entry.at, value });
  }
  return out;
}

/** A server-computed mean, or `null` where the series it describes is empty. */
function meanOf(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export interface ClientRow {
  /** Unique across the whole roster. Never `id` — see the header note. */
  readonly rowKey: string;
  /**
   * The id a client detail can be opened under, or `null` when this row has none to open. See
   * `openableCustomerId` — an invitation to an address that already had an account is the `null`.
   */
  readonly customerId: number | null;
  readonly invitationId: number | null;
  readonly fullName: string;
  /**
   * Up to two letters for the avatar, as the backend drew them. Empty only when the row carries no
   * person at all — a name, or the e-mail standing in for one, always yields letters.
   */
  readonly initials: string;
  /**
   * The client's photo, or `null` when they have none — which is every pending invitation, because
   * an unclaimed address has no account for the directory to resolve a picture from. The avatar
   * falls back to `initials`; it never renders a broken image.
   */
  readonly avatarUrl: string | null;
  readonly email: string | null;
  /**
   * `null` when the wire sent a value this app has no name for. The status cell then renders
   * nothing rather than guessing, because all three guesses are a claim about the client's
   * standing and two of them would be false.
   */
  readonly status: ClientStatus | null;
  /** When the invitation was sent. Only ever set on an `invited` row. */
  readonly invitedAt: string | null;
  readonly goal: string | null;
  /** `null` for every self-paced client — `standing` answers instead. Never both (R15). */
  readonly compliance: ClientCompliance | null;
  /**
   * Where a self-paced client stands (R16), and `null` for every dated one. Its presence IS the
   * roster's mode discriminator: `GetCustomerResponse` carries no `schedulingMode`, and this is the
   * field the backend adds for exactly this question.
   */
  readonly standing: ClientStanding | null;
  /**
   * The window held nothing to miss — `complianceTotal: 0`, which is what a client the trainer has
   * only just added looks like.
   *
   * It exists because `compliance: null` cannot carry the difference on its own, and two surfaces
   * need it: the attendance cell draws no ring either way, but the "Nieaktywni" tab must exclude
   * this row and must NOT exclude a client who was simply never scored.
   */
  readonly nothingDue: boolean;
  readonly lastWorkoutAt: string | null;
  readonly planName: string | null;
  /** One entry per KIND, most urgent first, in the order the backend ranked them. */
  readonly tasks: readonly ClientTask[];
  /**
   * The churn signal, as the reason to SAY — `null` when this client is not at risk.
   *
   * KANON: "Ryzyko mówi słowami". The row carries the reason key and the table renders
   * `clients.tasks.reasons.<reason>.title`, which is the same sentence the mobile app shows.
   */
  readonly riskReason: AttentionReason | null;
  /** Sleep in HOURS, oldest first, at most seven. Empty where the client reported none. */
  readonly sleep: readonly WellbeingReading[];
  /**
   * The mean of exactly `sleep`, as the SERVER computed it — never derived here.
   *
   * The rule for a mean (which readings, what rounding) has to live in one place or the web and the
   * phone print different numbers for the same row. `null` when the series is empty; that is an
   * empty state, not a zero.
   */
  readonly avgSleepHours: number | null;
  /** Mood on the contract's 1–10 scale, oldest first, at most seven. */
  readonly mood: readonly WellbeingReading[];
  /** The mean of exactly `mood`, on the same terms as `avgSleepHours`. */
  readonly avgMood: number | null;
  /**
   * Did this client finish a workout inside the last seven of THEIR days (R10, R21's window)?
   *
   * `null` where the backend sent nothing — a pending invitation, a caller that did not ask for
   * metrics, or a build of the server older than 15 September 2026. Three absences, one meaning to
   * a reader: this row cannot say. It is deliberately NOT folded to `false`, because "not active"
   * and "we do not know" are the difference between a counter the trainer can trust and one that
   * quietly reports every client as gone the day the field is dropped from the contract.
   *
   * There is no new-client grace here, unlike the lapsed-activity DETECTOR: this is a counter, not
   * a task, and a client added this morning genuinely has not trained this week.
   */
  readonly activeLast7Days: boolean | null;
  /**
   * This week's sessions — done over due-so-far — on the trainer's week and the client's day, the
   * same reading the 30-day column already uses.
   *
   * `null` for every self-paced client (R15: no adherence figure, ever) and for every row the
   * backend sent no counter for. The two halves arrive together or not at all, which is what the
   * contract states, and a row carrying one of them is read as carrying neither.
   */
  readonly weekCompliance: { readonly done: number; readonly total: number } | null;
}

/**
 * How the roster is doing RIGHT NOW, as the two figures the trainer's home draws beside its ring.
 *
 * Folded from the rows rather than read from a second endpoint, for the reason the three counts
 * below it are: one read, one truth, and no figure that can disagree with the list under it.
 *
 * **Active-status rows only.** A pending invitation has no training to count and an ended
 * relationship is not on the roster at all; including either would put a denominator on screen that
 * no amount of coaching can move.
 */
export interface RosterPulse {
  /** How many active clients trained in the last seven days, out of how many there are. */
  readonly activeClients: { readonly done: number; readonly total: number };
  /** This week's completed sessions over this week's due-so-far, summed across active clients. */
  readonly weekSessions: { readonly done: number; readonly total: number };
}

export interface ClientRoster {
  readonly rows: readonly ClientRow[];
  readonly activeCount: number;
  readonly totalCount: number;
  /**
   * Rows standing on an unaccepted invitation — KANON klienci v2's second statistic, which replaced
   * v1's task count in the header band. Folded from the rows for the same reason the other two are:
   * one read, one truth, and no figure that can disagree with the table under it.
   */
  readonly invitedCount: number;
  /** The two figures the home's tiles draw. See `RosterPulse`. */
  readonly pulse: RosterPulse;
}

function trimmedOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * The row's identity.
 *
 * An invited row is keyed on its invitation, a real client on its customer id, and the two
 * namespaces are prefixed so they can never collide with each other either — the same shape the
 * backend's own anchor keys use.
 */
function rowKeyFor(dto: CustomerDto): string {
  if (dto.invitationId != null) return `${INVITATION_ANCHOR_PREFIX}${dto.invitationId}`;
  return `customer:${dto.id ?? 0}`;
}

/**
 * **Whether this row opens a client detail, and under which id** — R2's authoring gate, the same
 * one the mobile app reads as `canAssignWork`.
 *
 * `GET /customer` returns two kinds of `invited` row that look alike, both with the real person's
 * id. A roster entry the trainer created has an open engagement from that moment, so everything —
 * the detail, a plan, a survey, a note — is available against it before the client answers. An
 * offer to an address that already had an account opens no engagement until its holder accepts,
 * and every detail read for that person is refused (a 404) until then. `hasOpenEngagement` is the
 * only field that tells the two apart; `hasPersonalTrainer` names the trainer ASKING on an offer,
 * so reading it gives the wrong answer.
 *
 * Only an invitation is asked the question. A row with no invitation attached is a client on this
 * trainer's roster, which is what an open engagement is. And an invitation whose row does not say
 * `true` stays closed: an absent flag is not permission to fire reads the backend refuses.
 */
export function openableCustomerId(dto: CustomerDto): number | null {
  if (dto.invitationId == null) return dto.id ?? null;
  if (dto.hasOpenEngagement !== true) return null;
  return typeof dto.id === 'number' && dto.id > 0 ? dto.id : null;
}

/**
 * Nothing was DUE in the measurement window.
 *
 * `CustomerComplianceMath.Pct` returns 0 when its denominator is 0, and the metric block is written
 * for every roster row rather than only for scored ones — so a client invited this morning arrives
 * as a perfectly well-formed `compliancePct: 0, complianceTotal: 0`. Read literally, that paints a
 * full red ring at 0 %, files them under "Nieaktywni" and calls them churn thirty seconds after the
 * trainer added them.
 *
 * This is NOT the same as absent metrics. An absent block means the row was never scored, which is
 * what a long-standing client with no completed workouts looks like, and that row genuinely has
 * lapsed. `total: 0` means the window held nothing to miss.
 */
function nothingWasDue(dto: CustomerDto): boolean {
  return dto.complianceTotal === 0;
}

/**
 * This week's two counters, or `null`.
 *
 * Both or neither, which is what `CustomerListMetrics` states: the backend writes the pair from one
 * `ComputeAdherence` call and omits both where it is not applicable. A row that arrived with one of
 * them is a contract that changed under us, and the honest reading of half a fraction is no
 * fraction — a "3 z" on a tile is worse than an em dash.
 *
 * `standing !== null` short-circuits ahead of the field read for the same reason `complianceOf`
 * does: a self-paced client has no adherence figure and never will (R15), so the row can never
 * carry one whichever way it arrived.
 */
function weekComplianceOf(
  dto: CustomerDto,
  standing: ClientStanding | null,
): { done: number; total: number } | null {
  if (standing !== null) return null;
  const done = dto.weekComplianceDone;
  const total = dto.weekComplianceTotal;
  if (typeof done !== 'number' || typeof total !== 'number') return null;
  return { done, total };
}

function complianceOf(dto: CustomerDto, standing: ClientStanding | null): ClientCompliance | null {
  // A self-paced client has no adherence figure and never will (R15) — the backend sends this
  // standing exactly where it sends a null percent, and says the two are "present exactly when the
  // other is absent". Enforced here rather than trusted, so the row can never carry both: a percent
  // rendered beside a standing would be the lie R15 exists to prevent, whichever way it arrived.
  if (standing !== null) return null;
  // Absent key, not null: `undefined` is "never measured", `null` would be the backend
  // contradicting its own JsonIgnore. Both are treated as no data; only a number is data.
  if (typeof dto.compliancePct !== 'number') return null;
  // A percentage over nothing is not a percentage. `done: 0, total: 12` is a real and terrible
  // score and keeps its ring; `total: 0` has no figure at all.
  if (nothingWasDue(dto)) return null;
  return {
    pct: dto.compliancePct,
    done: typeof dto.complianceDone === 'number' ? dto.complianceDone : null,
    total: typeof dto.complianceTotal === 'number' ? dto.complianceTotal : null,
    trend: typeof dto.complianceTrend === 'number' ? dto.complianceTrend : null,
    tone: complianceTone(dto.compliancePct),
  };
}

/**
 * Which row an attention item belongs to, expressed as that row's key — or `null` when the item
 * cannot be attached to anything on this roster.
 *
 * `PublicFormLeadSubmitted` is subject-less too — a lead is nobody's row yet — and answers `null`
 * here, so it never reaches the roster.
 *
 * `InvitationPending` is the other subject-less reason: its `subject.customerId` is `null` by
 * design (`AttentionAnchors.EmbeddedCustomerId` returns null for it explicitly). A join that fell
 * back to `customerId ?? 0` would attach EVERY pending invitation to EVERY invited row, because
 * every invited row's `id` is also 0. The invitation id lives in the anchor instead, as
 * `invitation:{invitationId}` — which is exactly the key `rowKeyFor` mints, so the join is a
 * string equality and no parsing is needed.
 */
function rowKeyForAttentionItem(item: AttentionQueueItem): string | null {
  const customerId = item.subject?.customerId;
  if (typeof customerId === 'number') return `customer:${customerId}`;
  const anchorKey = item.anchorKey;
  if (anchorKey?.startsWith(INVITATION_ANCHOR_PREFIX)) return anchorKey;
  return null;
}

function tasksByRowKey(
  attention: AttentionQueueResponse | null | undefined,
): Map<string, ClientTask[]> {
  const byRowKey = new Map<string, ClientTask[]>();
  for (const item of attention?.items ?? []) {
    const reason = item.reason == null ? undefined : REASON_BY_WIRE_VALUE[item.reason];
    if (reason === undefined) continue;

    const rowKey = rowKeyForAttentionItem(item);
    if (rowKey === null) continue;

    // Arrival order is preserved on purpose: the backend already ranked the queue by severity and
    // then by recency (`AttentionQueueProcessor.RankAndCap`), so the first reason a row receives
    // is its most urgent one, and the first item of a reason is that reason's newest signal.
    const bucket = byRowKey.get(rowKey) ?? [];
    if (bucket.length === 0) byRowKey.set(rowKey, bucket);

    const existing = bucket.find((task) => task.reason === reason);
    if (existing === undefined) {
      bucket.push({
        reason,
        count: 1,
        severity: item.severity ?? 0,
        latestSignalAtUtc: item.signalAtUtc ?? null,
      });
      continue;
    }

    bucket[bucket.indexOf(existing)] = { ...existing, count: existing.count + 1 };
  }
  return byRowKey;
}

/** Compliance below this, with an open missed-workout task, marks a client at risk. */
const RISK_COMPLIANCE_THRESHOLD = 50;

/**
 * What the churn rule needs to know about the row the task belongs to.
 *
 * `standing` left on 16 September 2026 with `noUpcomingSessions`: it was here only to tell a
 * self-paced plan's resting state from a real scheduling gap, and no reason asks that question any
 * more.
 */
interface RiskContext {
  readonly compliance: ClientCompliance | null;
  /** The measurement window held nothing to miss — see `nothingWasDue`. */
  readonly nothingDue: boolean;
}

/**
 * Whether one task means this client is churning — mobile's `riskJoin`, with ONE deliberate
 * divergence, stated below.
 *
 * At-risk is gated by the reason, not merely by a task existing. Exactly TWO reasons qualify since
 * 16 September 2026: a lapsed client, and a missed workout the metrics corroborate — compliance
 * under 50 %, or a negative trend. A high-compliance, improving client who missed one session is a
 * heads-up, not churn, and stays off the risk line. See `noUpcomingSessions` in the switch for the
 * third one's removal and the reason behind it.
 *
 * ── The divergence: `lapsedActivity` on a row that had nothing to do ─────────────────────────
 * `LapsedActivity` fires on 14 days without a COMPLETED workout, so it fires on a client the
 * trainer added five minutes ago just as surely as on one who walked away — the difference is not
 * in the queue, it is in `complianceTotal: 0`, which says the window held nothing to miss. Calling
 * the new client churn puts them in the top band of "Wymagający uwagi najpierw", above a client at
 * 80 % who really is there, and prints "Brak aktywności" in danger ink under a name the trainer
 * typed this morning.
 *
 * It is the SAME rule the "Nieaktywni" tab already applies (`rosterView.isLapsed`), and it has to
 * be, because the tab and the risk line make the same claim about the same client on the same
 * screen. Mobile's `riskJoin` has this hole — it returns true for `LapsedActivity` unconditionally
 * and its list item carries no equivalent of `nothingDue` — and it is NOT copied for parity: the
 * two apps must not disagree about who is leaving, and the one that says a fresh signup is leaving
 * is the one that is wrong. Raised as an upstream follow-up.
 *
 * The task itself survives — it is still a job the trainer can act on and it still counts toward
 * the header's figure. Only the churn CLAIM goes.
 */
function isRiskTask(task: ClientTask, client: RiskContext): boolean {
  switch (task.reason) {
    case 'lapsedActivity':
      return !client.nothingDue;
    // `noUpcomingSessions` is NOT a churn reason, as of 16 September 2026.
    //
    // It used to be one, gated on the plan not being self-paced. The owner's instruction was to
    // rethink when the roster is allowed to print red at all, and this reason does not survive the
    // question: an empty calendar is a gap in the TRAINER's own scheduling, not evidence that the
    // client is leaving. The client may have trained yesterday and be perfectly engaged. Painting
    // their name red says something about them that is not true, and it did so on every load until
    // the trainer booked something.
    //
    // The task itself survives untouched — it is still a real job, it still appears in the
    // attention queue, it still drives the "wymagajacy uwagi" ordering, and the client's own card
    // still lists it. Only the CHURN CLAIM goes, which is the same distinction `lapsedActivity`
    // already draws below for a client who had nothing to miss.
    case 'missedWorkout':
      return (
        (client.compliance !== null && client.compliance.pct < RISK_COMPLIANCE_THRESHOLD) ||
        (client.compliance?.trend != null && client.compliance.trend < 0)
      );
    default:
      return false;
  }
}

/**
 * Build the roster.
 *
 * `attention` is `null` when that read failed or has not landed — the rows still build, with no
 * tasks and no risk lines. The roster read alone gates the error state: a failed queue costs the
 * trainer the risk lines, never the client list, which is how the mobile app degrades and is the
 * behaviour this projection keeps.
 *
 * There is no roster-wide task COUNT any more. `taskCount`, `attentionAvailable` and the
 * `totalTasks` fold that produced them were KANON klienci v1's third header figure; v2 replaced it
 * with the invited count, nothing has read the three since, and they went on 2 September 2026.
 */
export function buildClientRoster(
  customers: readonly CustomerDto[] | null | undefined,
  attention: AttentionQueueResponse | null | undefined,
): ClientRoster {
  const taskBuckets = tasksByRowKey(attention);

  const rows: ClientRow[] = (customers ?? []).map((dto) => {
    const rowKey = rowKeyFor(dto);
    const email = trimmedOrNull(dto.email);
    // Read off the wire, not composed. `person` is the 25 August 2026 contract's one shape for
    // "what is this person called and what do they look like": the backend composes the display
    // name (falling back to the e-mail address when no name is set), draws the initials from it,
    // and resolves the photo. Composing any of the three here again is what made the same client
    // read AK in one app and AM in another.
    const person = dto.person;
    const fullName = trimmedOrNull(person?.fullName) ?? '';
    const standing = clientStanding(dto.planStanding);
    const compliance = complianceOf(dto, standing);
    const nothingDue = nothingWasDue(dto);
    const tasks = taskBuckets.get(rowKey) ?? [];
    // Tasks arrive severity-ranked, so the FIRST qualifying one is the most severe — and its
    // reason is the sentence the row shows.
    const risk = tasks.find((task) => isRiskTask(task, { compliance, nothingDue }));

    return {
      rowKey,
      customerId: openableCustomerId(dto),
      invitationId: dto.invitationId ?? null,
      fullName,
      initials: trimmedOrNull(person?.initials) ?? '',
      avatarUrl: trimmedOrNull(person?.avatarUrl),
      email,
      status: clientStatusOf(dto.status),
      invitedAt: dto.invitationCreatedAt ?? null,
      goal: trimmedOrNull(dto.goal),
      compliance,
      standing,
      nothingDue,
      lastWorkoutAt: dto.lastWorkoutAt ?? null,
      planName: trimmedOrNull(dto.planName),
      tasks,
      riskReason: risk?.reason ?? null,
      sleep: readingsOf(dto.sleepReadings),
      avgSleepHours: meanOf(dto.avgSleepHours),
      mood: readingsOf(dto.moodReadings),
      avgMood: meanOf(dto.avgMood),
      activeLast7Days: typeof dto.activeLast7Days === 'boolean' ? dto.activeLast7Days : null,
      weekCompliance: weekComplianceOf(dto, standing),
    };
  });

  return { rows, ...foldRoster(rows) };
}

/**
 * The home's two figures, without the rest of the roster.
 *
 * The trainer's home reads nothing but these — it was building the whole roster and taking `.pulse`
 * off it, which made the tree's shape part of an interface that is two fractions wide.
 */
export function rosterPulseOf(customers: readonly CustomerDto[] | null | undefined): RosterPulse {
  return buildClientRoster(customers, null).pulse;
}

/**
 * **How many whole days since each client last finished a workout**, keyed by customer id.
 *
 * The attention queue says a client has lapsed; it does not say for how long — its payload carries
 * no date for that reason. The roster does, in `lastWorkoutAt`, and the home already reads the
 * roster for its tiles, so the number the trainer actually wants ("bez treningu od 41 dni") is a
 * join rather than a request.
 *
 * A client with NO completed workout is absent from the map rather than present with a huge number:
 * "bez treningu od 812 dni" counted from an account's creation is a fact about the account, not
 * about the coaching, and the sentence that stands in for it says so in words instead.
 *
 * CALENDAR days, against the reader's clock — the same count the roster's „ostatni trening" column
 * (`elapsedSince`) and the plan standing's „Ostatni trening: N dni temu" (`daysSinceLastTraining`,
 * the backend's local-day count) state. It was whole 24-hour blocks, floored, so a session logged at
 * 20:00 read „19 dni bez treningu" on the home's alert beside „20 dni temu" on the client's card.
 */
export function daysSinceLastWorkout(
  customers: readonly CustomerDto[] | null | undefined,
  now: Date,
): ReadonlyMap<number, number> {
  const days = new Map<number, number>();
  for (const dto of customers ?? []) {
    if (dto.invitationId != null || typeof dto.id !== 'number') continue;
    const elapsed = calendarDaysSince(dto.lastWorkoutAt ?? null, now);
    if (elapsed !== null) days.set(dto.id, elapsed);
  }
  return days;
}

/** An invitation's two instants, as the roster states them. */
export interface InvitationClock {
  /** When it was sent, or `null` where the wire did not say. */
  readonly sentAt: string | null;
  /**
   * When the OFFER lapses — fourteen days from sending (client-onboarding R4), and NOT moved by a
   * resend. It is the offer's clock, never the mailed link's, which is Supabase's and shorter.
   */
  readonly expiresAt: string | null;
}

/**
 * The clock of ONE invitation, read off the roster the screen already holds.
 *
 * A lookup by invitation id rather than a field on `ClientRow`, because only the invitation's task
 * card asks it, and the roster row is the shape a dozen surfaces construct. `null` where the roster
 * does not carry that invitation — it was answered, withdrawn, or the read has not landed.
 */
export function invitationClock(
  customers: readonly CustomerDto[] | null | undefined,
  invitationId: number,
): InvitationClock | null {
  const dto = (customers ?? []).find((entry) => entry.invitationId === invitationId);
  if (dto === undefined) return null;
  return {
    sentAt: dto.invitationCreatedAt ?? null,
    expiresAt: dto.invitationExpiresAt ?? null,
  };
}

/**
 * Every figure the roster states about itself, in ONE pass.
 *
 * It used to be five passes over the rows and three intermediate arrays — two `filter().length`
 * for the two counts, then an `active` array, a `measured` array and two `reduce`s for the pulse.
 * The counters are independent of each other, so one loop states all of them and the answer is
 * unchanged.
 *
 * `?? 0` on each session numerator and denominator is the whole of the self-paced handling, and it
 * is the right answer rather than a shortcut: a self-paced client contributes nothing to a sessions
 * ratio because no session was ever due of them (R15), and adding them to the denominator would
 * print a shortfall the trainer cannot act on. They still count as a CLIENT in the activity
 * figure's total, because they are on the roster and `activeLast7Days` is answered for them — that
 * counter asks whether anybody trained, which is a question self-paced training has an answer to.
 *
 * Only the clients the server actually answered the activity question for reach that pair.
 * `activeLast7Days` is `[JsonIgnore(WhenWritingNull)]`, so a build that does not send it yet
 * arrives as an ABSENT KEY on every row — and counting those rows in the denominator alone prints
 * "0 z 7" with a full red ring, which is a claim about coaching that nobody made. With no measured
 * row the pair is 0/0, which is the figure the tile refuses to draw and answers with a sentence
 * instead.
 */
function foldRoster(rows: readonly ClientRow[]): Omit<ClientRoster, 'rows'> {
  let activeCount = 0;
  let invitedCount = 0;
  let measured = 0;
  let measuredActive = 0;
  let sessionsDone = 0;
  let sessionsTotal = 0;

  for (const row of rows) {
    if (row.status === 'invited') invitedCount += 1;
    if (row.status !== 'active') continue;

    activeCount += 1;
    if (row.activeLast7Days !== null) {
      measured += 1;
      if (row.activeLast7Days) measuredActive += 1;
    }
    sessionsDone += row.weekCompliance?.done ?? 0;
    sessionsTotal += row.weekCompliance?.total ?? 0;
  }

  return {
    activeCount,
    invitedCount,
    totalCount: rows.length,
    pulse: {
      activeClients: { done: measuredActive, total: measured },
      weekSessions: { done: sessionsDone, total: sessionsTotal },
    },
  };
}

